import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, CalendarClock, Crown, ChevronDown, Check, Wine, Music, Info, AlertTriangle, X, Clock, BellRing } from "lucide-react";
import QRCode from "react-qr-code";
import { api, fmtDateTime, fmtDate, avatarSrc } from "../lib/api";
import { CheckinHistory, bundleTitle } from "../components/CheckinHistory";
import { useAuth } from "../context/AuthContext";
import { Screen } from "../components/ui-kit";
import LevelUpDialog from "../components/LevelUpDialog";
import { toast } from "sonner";

const daysLeft = (iso) => Math.max(0, Math.ceil((new Date(iso) - new Date()) / 864e5));

function fmtOpens(iso) {
  let diff = Math.max(0, new Date(iso).getTime() - Date.now());
  const d = Math.floor(diff / 86400000); diff -= d * 86400000;
  const h = Math.floor(diff / 3600000); diff -= h * 3600000;
  const m = Math.floor(diff / 60000);
  const rel = d > 0 ? `${d}d ${h}h` : (h > 0 ? `${h}h ${m}m` : `${m}m`);
  const wd = new Date(iso).toLocaleString("en-SG", { weekday: "short", timeZone: "Asia/Singapore" });
  const tm = new Date(iso).toLocaleString("en-SG", { hour: "numeric", minute: "2-digit", hour12: true, timeZone: "Asia/Singapore" }).replace(":00", "").replace(/\s/g, "").toLowerCase();
  return { label: `Opens ${wd} ${tm}`, rel };
}

function OpensSoon({ watched }) {
  const navigate = useNavigate();
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((n) => n + 1), 60000); return () => clearInterval(t); }, []);
  if (!watched || watched.length === 0) return null;
  return (
    <div data-testid="opens-soon" className="mt-5">
      <p className="text-[10px] uppercase tracking-wide text-[#A19DB0] mb-2 flex items-center gap-1.5"><BellRing size={13} className="text-[#D4AF37]" /> Waiting for sales to open</p>
      <div className="flex gap-2 flex-wrap">
        {watched.map((w) => { const f = fmtOpens(w.opens_at); return (
          <button key={w.package_id} onClick={() => navigate("/packages")} data-testid={`opens-soon-${w.package_id}`}
            className="flex items-center gap-2 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] pl-2.5 pr-3.5 py-2 active:scale-95 transition-transform">
            <Clock size={15} className="text-[#D4AF37] shrink-0" />
            <span className="text-left min-w-0">
              <span className="block text-xs font-semibold leading-tight truncate max-w-[150px]">{w.name}</span>
              <span className="block text-[10px] text-[#D4AF37] leading-tight">{f.label} · in {f.rel}</span>
            </span>
          </button>
        ); })}
      </div>
    </div>
  );
}
const ROLE_LABEL = { guest: "Guest", member: "Member", student: "Student", vip: "VIP", instructor: "Instructor", artist: "Artist", admin: "Admin", super_admin: "Super Admin" };
const greeting = () => { const h = new Date().getHours(); return h < 12 ? "Good morning" : h < 18 ? "Good afternoon" : "Good evening"; };

function SetupChecklist() {
  const { user, setUser, checkAuth } = useAuth();
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);
  const needEmail = user?.email_verified === false;
  const needGender = !user?.gender;
  if (!needEmail && !needGender) return null;
  const send = async () => { setBusy(true); try { await api.post("/auth/resend-otp"); setSent(true); toast.success("Code sent to your email"); } catch { toast.error("Couldn't send code"); } finally { setBusy(false); } };
  const verify = async () => { setBusy(true); try { await api.post("/auth/verify-otp", { email: user.email, code: code.trim() }); await checkAuth(); toast.success("Email verified!"); } catch { toast.error("Invalid code"); } finally { setBusy(false); } };
  const setGender = async (v) => { try { const r = await api.put("/profile", { gender: v }); setUser(r.data); toast.success("Saved"); } catch { toast.error("Couldn't save"); } };
  return (
    <div data-testid="home-setup-checklist" className="mt-5 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.08] p-5">
      <p className="font-display text-base font-semibold flex items-center gap-2"><AlertTriangle size={16} className="text-[#D4AF37]" /> Finish setup to buy passes</p>
      <p className="text-[#A19DB0] text-xs mt-1">Verify your email and tell us your gender before purchasing any pass.</p>
      {needEmail && (
        <div className="mt-4" data-testid="home-verify-email">
          <p className="text-sm font-medium">1. Verify your email <span className="text-[#A19DB0] font-normal">({user.email})</span></p>
          {!sent ? (
            <button onClick={send} disabled={busy} data-testid="home-send-otp" className="mt-2 rounded-full bg-[#D4AF37] text-black text-sm font-semibold px-4 py-2 disabled:opacity-60">Send verification code</button>
          ) : (
            <>
              <div className="flex gap-2 mt-2">
                <input value={code} onChange={(e) => setCode(e.target.value)} data-testid="home-otp-input" placeholder="6-digit code" className="flex-1 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-sm outline-none focus:border-[#D4AF37]" />
                <button onClick={verify} disabled={busy} data-testid="home-verify-otp" className="rounded-xl bg-[#D4AF37] text-black font-semibold px-4 text-sm disabled:opacity-60">Verify</button>
              </div>
              <button onClick={send} disabled={busy} className="text-xs text-[#A19DB0] underline mt-2">Resend code</button>
            </>
          )}
        </div>
      )}
      {needGender && (
        <div className="mt-4" data-testid="home-gender">
          <p className="text-sm font-medium">{needEmail ? "2. " : "1. "}Select your gender</p>
          <div className="flex gap-2 mt-2">
            {[["M", "Male (M)"], ["F", "Female (F)"]].map(([v, l]) => (
              <button key={v} onClick={() => setGender(v)} data-testid={`home-gender-${v}`} className="flex-1 rounded-xl border border-white/15 bg-white/5 py-2 text-sm hover:border-[#D4AF37]/60 transition-colors">{l}</button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PassCard({ p }) {
  const [open, setOpen] = useState(false);
  const hasIncl = (p.inclusions || []).length > 0;
  let title = p.package_name;
  if (p.group === "classes") title = `${p.credits_left}/${p.credits_total} · ${p.package_name || "Class Pass"}`;
  else if (p.bundle_size > 1) title = bundleTitle(p);
  else if (p.group === "socials") title = p.credits_total > 1 ? `${p.package_name} · ${p.credits_left}/${p.credits_total}` : `${p.package_name} · ${p.credits_left} entr${p.credits_left !== 1 ? "ies" : "y"}`;
  return (
    <div data-testid={`pass-${p.id}`} className="rounded-2xl border border-white/10 bg-white/5 p-4 relative overflow-hidden">
      <span className="absolute left-0 top-0 bottom-0 w-1.5" style={{ background: p.color || "#9D4EDD" }} />
      <div className="flex items-start justify-between gap-2 pl-2">
        <div className="min-w-0">
          <p className="font-medium">{title}{p.option ? ` · ${p.option}` : ""}</p>
          <p className="text-[#A19DB0] text-xs mt-0.5">Expires in {daysLeft(p.expires_at)}d · {fmtDate(p.expires_at)}</p>
          {p.valid_dates?.length > 0 && <p className="text-[#D4AF37] text-xs mt-0.5">Valid only on {p.valid_dates.map(fmtDate).join(" & ")}</p>}
          {p.social_title && <p className="text-[#D4AF37] text-xs mt-0.5">For {p.social_title}{p.social_date ? ` · ${fmtDate(p.social_date)}` : ""}</p>}
          {p.shared_by && <p className="text-[#D4AF37] text-xs mt-0.5">Shared by {p.shared_by}</p>}
          {p.drink_total > 0 && <p className="text-[#D4AF37] text-xs mt-1 flex items-center gap-1"><Wine size={13} /> {p.drink_left}/{p.drink_total} drink coupons</p>}
        </div>
        <span className="text-[10px] uppercase tracking-wide text-[#A19DB0] bg-white/5 rounded-full px-2 py-1 shrink-0">{p.group}</span>
      </div>
      <CheckinHistory p={p} />
      {hasIncl && (
        <>
          <button onClick={() => setOpen((o) => !o)} data-testid={`pass-expand-${p.id}`} className="flex items-center gap-1 text-[#D4AF37] text-sm mt-2 pl-2">
            What you get <ChevronDown size={15} className={`transition-transform ${open ? "rotate-180" : ""}`} />
          </button>
          {open && (
            <ul data-testid={`pass-inclusions-${p.id}`} className="mt-2 space-y-1.5 pl-2">
              {p.inclusions.map((it, i) => <li key={`${p.id}-incl-${i}`} className="flex gap-2 text-sm text-[#E8E4F0]"><Check size={15} className="text-[#D4AF37] mt-0.5 shrink-0" /> {it}</li>)}
            </ul>
          )}
        </>
      )}
    </div>
  );
}

function VipProgress() {
  const [p, setP] = useState(null);
  const [showInfo, setShowInfo] = useState(false);
  useEffect(() => { api.get("/vip/progress").then((r) => setP(r.data)).catch(() => {}); }, []);
  if (!p || (p.is_vip && p.lifetime)) return null;
  const pct = Math.min(100, Math.round((p.count / p.target) * 100));
  const isMaintain = p.is_vip;
  const detailMain = isMaintain
    ? (p.remaining > 0
        ? `Attend ${p.remaining} more social${p.remaining !== 1 ? "s" : ""} to keep VIP — ${p.target} every 6 months.`
        : "You're on track — VIP stays active this period!")
    : (p.remaining > 0
        ? `Attend ${p.remaining} more social${p.remaining !== 1 ? "s" : ""} to unlock VIP.`
        : "You've hit the target — VIP unlocks on your next social check-in!");
  const detailExpiry = isMaintain
    ? (p.expires_at ? `Renew by ${fmtDate(p.expires_at)}` : "")
    : (p.next_expiry ? `Counts reset ${fmtDate(p.next_expiry)}` : "");
  return (
    <>
      <button type="button" onClick={() => setShowInfo(true)} data-testid="vip-progress"
        className={`w-full text-left mt-5 rounded-2xl border p-5 active:scale-[0.99] transition-transform ${isMaintain ? "border-[#EC4899]/40 bg-[#EC4899]/[0.08]" : "border-[#D4AF37]/30 bg-[#D4AF37]/[0.06]"}`}>
        <div className="flex items-center justify-between gap-2">
          <p className="font-display text-base font-semibold flex items-center gap-2">
            <Crown size={17} className={isMaintain ? "text-[#EC4899]" : "text-[#D4AF37]"} />
            {isMaintain ? "Keep your VIP status" : "Your path to VIP"}
            <Info size={14} className="text-[#A19DB0]" />
          </p>
          <span className="text-sm font-bold" data-testid="vip-progress-count">{p.count}/{p.target}</span>
        </div>
        <div className="mt-3 h-2.5 rounded-full bg-white/10 overflow-hidden">
          <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: isMaintain ? "#EC4899" : "#D4AF37" }} />
        </div>
      </button>
      {showInfo && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowInfo(false)} data-testid="vip-info-modal">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-[#D4AF37]/30 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3">
              <p className="font-display text-lg font-semibold flex items-center gap-2">
                <Crown size={19} className={isMaintain ? "text-[#EC4899]" : "text-[#D4AF37]"} />
                {isMaintain ? "Keep your VIP status" : "Your path to VIP"}
              </p>
              <button onClick={() => setShowInfo(false)} data-testid="vip-info-close" className="text-[#A19DB0]"><X size={18} /></button>
            </div>
            <p className="text-[#E8E4F0] text-sm">{detailMain}</p>
            {detailExpiry && <p className="text-xs font-medium mt-2" style={{ color: isMaintain ? "#EC4899" : "#D4AF37" }} data-testid="vip-info-expiry">{detailExpiry}</p>}
          </div>
        </div>
      )}
    </>
  );
}

const GROUP_STYLE = {
  socials: { tab: "#6d5efc", grad: "linear-gradient(160deg,#7b2ff7 0%,#4f3ff0 45%,#2f6bff 100%)" },
  classes: { tab: "#22c55e", grad: "linear-gradient(160deg,#37d67a 0%,#12b886 60%,#0f9d58 100%)" },
  weekender: { tab: "#14b8a6", grad: "linear-gradient(160deg,#22d3c5 0%,#12b886 60%,#0ca678 100%)" },
  events: { tab: "#D4AF37", grad: "linear-gradient(160deg,#e0a815 0%,#c99700 60%,#a97d00 100%)" },
};
const GROUP_FALLBACK = { tab: "#9D4EDD", grad: "linear-gradient(160deg,#9D4EDD 0%,#7b2ff7 60%,#4f3ff0 100%)" };

function classTicket(pass) {
  const s = (pass?.package_name || pass?.class_level || "").toLowerCase();
  if (s.includes("advance") || s.includes("focus")) return { title: "ADVANCE CLASS", grad: "linear-gradient(160deg,#ff4d4d 0%,#f5327d 70%,#e11d74 100%)" };
  if (s.includes("interm")) return { title: "INTERMEDIATE CLASS", grad: "linear-gradient(160deg,#f5d020 0%,#e0a815 60%,#c99700 100%)" };
  return { title: "BEGINNER CLASS", grad: "linear-gradient(160deg,#37d67a 0%,#12b886 60%,#0f9d58 100%)" };
}

function ScanTicket({ user, passes }) {
  const [cat, setCat] = useState(null);
  const [groups, setGroups] = useState([]);
  const navigate = useNavigate();
  useEffect(() => { api.get("/pkg-groups").then((r) => setGroups(r.data || [])).catch(() => {}); }, []);

  // Organic + purchase-gated: only groups the admin created AND the member owns a pass in.
  const owned = groups.filter((g) => passes.some((p) => p.group === g.key));
  if (owned.length === 0) return null;

  const g = cat ? owned.find((x) => x.key === cat) : null;
  const style = g ? (GROUP_STYLE[g.key] || GROUP_FALLBACK) : null;
  const pass = g ? passes.find((p) => p.group === g.key) : null;
  let grad = "", title = "", subtitle = "";
  if (g && g.key === "classes") { const lv = classTicket(pass); grad = lv.grad; title = lv.title; subtitle = "BACHATA CLASS"; }
  else if (g) { grad = style.grad; title = (pass?.package_name || g.label).toUpperCase(); subtitle = g.label.toUpperCase(); }

  return (
    <div className="mt-4" data-testid="scan-ticket-section">
      <div className="flex gap-1 bg-white/5 border border-white/10 rounded-full p-1 overflow-x-auto">
        {owned.map((gr) => (
          <button key={gr.key} onClick={() => setCat(cat === gr.key ? null : gr.key)} data-testid={`scan-cat-${gr.key}`}
            className={`flex-1 whitespace-nowrap rounded-full py-2 px-3 text-sm font-semibold transition-colors ${cat === gr.key ? "text-white" : "text-[#A19DB0]"}`}
            style={cat === gr.key ? { background: (GROUP_STYLE[gr.key] || GROUP_FALLBACK).tab } : {}}>
            {gr.label}
          </button>
        ))}
      </div>
      {!cat && <p className="text-center text-[#A19DB0] text-xs mt-3" data-testid="scan-ticket-hint">Tap a pass above to show your scan-in ticket.</p>}
      {cat && pass && (
        <div className="mt-4 flex justify-center">
          <div data-testid="scan-ticket" className="relative w-[280px] rounded-[26px] overflow-hidden shadow-[0_16px_50px_-12px_rgba(0,0,0,0.75)]" style={{ background: grad }}>
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(circle at 28% 18%, rgba(255,255,255,0.22), transparent 55%)" }} />
            <span className="absolute -top-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-[#050505] z-10" />
            <span className="absolute -bottom-4 left-1/2 -translate-x-1/2 w-9 h-9 rounded-full bg-[#050505] z-10" />
            <div className="relative pt-9 pb-2 flex flex-col items-center">
              <QRCode value={JSON.stringify({ uid: user.id, name: user.name })} size={140} fgColor="#FFFFFF" bgColor="transparent" />
              <p className="text-white text-[13px] tracking-[0.25em] uppercase mt-4" data-testid="scan-ticket-scanhere">Scan here!</p>
            </div>
            <div className="border-t border-dotted border-white/50 mx-6 my-3" />
            <div className="px-5 text-center">
              <p className="font-display text-xl font-extrabold text-white uppercase tracking-wide leading-tight" data-testid="scan-ticket-name">{title}</p>
              <p className="text-white/85 text-xs uppercase tracking-wide mt-1" data-testid="scan-ticket-subtitle">{subtitle}</p>
            </div>
            <div className="border-t border-dotted border-white/50 mx-6 my-3" />
            <div className="px-6 pb-7 flex items-baseline justify-center gap-3">
              <span className="text-[11px] font-bold tracking-widest text-white">NAME</span>
              <span className="text-sm italic text-white/90 truncate" data-testid="scan-ticket-member">{user.name}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function UpcomingRow({ label, title, date, Icon, color, testid }) {
  return (
    <div data-testid={testid} className="flex items-center gap-4 rounded-2xl bg-white/5 border border-white/10 p-4">
      <div className="w-11 h-11 rounded-xl flex items-center justify-center shrink-0" style={{ background: `${color}22` }}>
        <Icon size={18} style={{ color }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] uppercase tracking-wide text-[#A19DB0]">{label}</p>
        {title
          ? <><p className="font-medium truncate">{title}</p><p className="text-[#A19DB0] text-xs">{date}</p></>
          : <p className="text-[#A19DB0] text-sm">Nothing upcoming</p>}
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [passTab, setPassTab] = useState("socials");
  const [levelUp, setLevelUp] = useState(null);
  const [showVipPerks, setShowVipPerks] = useState(false);

  useEffect(() => { api.get("/dashboard").then((r) => { setData(r.data); const nu = r.data?.milestones?.newly_unlocked; if (nu) setLevelUp({ level: nu, label: r.data.milestones.newly_unlocked_label || nu }); }).catch(() => setData({})); }, []);
  const closeLevelUp = () => { setLevelUp(null); api.post("/milestones/ack").catch(() => {}); };

  const passes = data?.passes || [];
  const expiringDrinks = passes.filter((p) => (p.drink_left || 0) > 0 && daysLeft(p.expires_at) <= 7);
  const PASS_TABS = [
    { key: "socials", label: "Socials", match: (p) => p.group === "socials" },
    { key: "classes", label: "Classes", match: (p) => p.group === "classes" },
    { key: "events", label: "Events", match: (p) => p.group !== "socials" && p.group !== "classes" },
  ];
  const upcomingSocials = (data?.upcoming_events || []).filter((e) => e.category === "socials");
  const oneClass = (data?.upcoming_classes || [])[0];
  const oneSocial = upcomingSocials[0];
  const oneEvent = (data?.upcoming_events || []).filter((e) => e.category === "events")[0];

  return (
    <Screen>
      <LevelUpDialog open={!!levelUp} label={levelUp?.label} level={levelUp?.level} onClose={closeLevelUp} />
      {showVipPerks && (
        <div className="fixed inset-0 z-[80] bg-black/70 backdrop-blur-sm flex items-center justify-center p-6" onClick={() => setShowVipPerks(false)} data-testid="vip-perks-modal">
          <div className="w-full max-w-sm rounded-3xl bg-[#1a1424] border border-[#EC4899]/40 p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-1">
              <p className="font-display text-xl font-semibold flex items-center gap-2"><Crown size={20} className="text-[#EC4899]" fill="#EC4899" /> VIP member perks</p>
              <button onClick={() => setShowVipPerks(false)} data-testid="vip-perks-close" className="text-[#A19DB0]"><X size={18} /></button>
            </div>
            <ul className="mt-3 space-y-2.5 text-sm">
              <li className="flex gap-2"><Check size={16} className="text-[#EC4899] mt-0.5 shrink-0" /> Solo social pass at S$13{user.vip_lifetime ? " for life" : ""} — save S$2 every time</li>
              <li className="flex gap-2"><Check size={16} className="text-[#EC4899] mt-0.5 shrink-0" /> Exclusive — VIP pricing shown across the Packages page</li>
              <li className="flex gap-2"><Check size={16} className="text-[#EC4899] mt-0.5 shrink-0" /> Priority recognition — your VIP badge shows to the team</li>
            </ul>
            <button onClick={() => { setShowVipPerks(false); navigate("/packages?group=socials"); }} data-testid="vip-perks-cta" className="mt-5 w-full bg-[#EC4899] text-white font-semibold rounded-full py-3 active:scale-95 transition-transform">Use my VIP price</button>
          </div>
        </div>
      )}
      <div data-testid="home-hero" className="hero-card relative overflow-hidden rounded-3xl border border-[#D4AF37]/25 p-6 mb-2 purple-glow">
        <div className="absolute -right-10 -top-10 w-44 h-44 rounded-full bg-[#9D4EDD]/25 blur-3xl" />
        <div className="absolute -left-8 -bottom-10 w-36 h-36 rounded-full bg-[#D4AF37]/10 blur-3xl" />
        <div className="relative flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <img src={avatarSrc(user)} alt="" className={`w-14 h-14 rounded-full object-cover border-2 shrink-0 ${user.role === "vip" ? "border-[#EC4899]" : "border-[#D4AF37]/50"}`} />
            <div className="min-w-0">
              <p className="text-[#A19DB0] text-xs">{greeting()}</p>
              <p className="font-display text-2xl font-semibold leading-tight truncate">{user.name}</p>
              <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
                <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-semibold text-[#c79bff] bg-[#9D4EDD]/20 rounded-full px-2 py-0.5">{ROLE_LABEL[user.role] || user.role}</span>
                {user.role === "vip" && <span data-testid="vip-badge" className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wide font-bold text-white bg-[#EC4899] rounded-full px-2 py-0.5">VIP · Solo social $13</span>}
              </div>
            </div>
          </div>
          <button onClick={() => navigate("/notifications")} data-testid="notifications-bell" className="relative w-10 h-10 rounded-full bg-white/5 border border-white/10 flex items-center justify-center active:scale-90 transition-transform shrink-0">
            <Bell size={18} />
            {data?.unread_notifications > 0 && <span className="absolute -top-1 -right-1 w-5 h-5 text-[10px] rounded-full bg-[#D4AF37] text-black flex items-center justify-center font-bold">{data.unread_notifications}</span>}
          </button>
        </div>

        {user.role !== "artist" && <SetupChecklist />}
        {user.role !== "artist" && <ScanTicket user={user} passes={passes} />}
        {user.role !== "artist" && <VipProgress />}
        <div className="relative mt-4 flex justify-end">
          <button onClick={() => navigate("/wallet")} data-testid="dashboard-wallet" className="bg-[#D4AF37] text-black font-semibold rounded-full px-5 py-2.5 text-sm active:scale-95 transition-transform">Open Wallet</button>
        </div>
      </div>

      {user.role === "artist" && (
        <div data-testid="artist-access" className="mt-5 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/[0.06] p-5 text-center">
          <p className="font-display text-lg font-semibold text-[#D4AF37]">Artist Access</p>
          <p className="text-[#E8E4F0] text-sm mt-1">Show this to enter all Socials &amp; Events.</p>
          <div className="bg-white p-4 rounded-2xl inline-block mt-4"><QRCode value={JSON.stringify({ uid: user.id, name: user.name })} size={160} fgColor="#0D0B14" bgColor="#FFFFFF" /></div>
          <p className="text-[#A19DB0] text-xs mt-3">{user.name}</p>
        </div>
      )}

      {user.role !== "artist" && <OpensSoon watched={data?.watched_tickets} />}
      {expiringDrinks.length > 0 && (
        <div data-testid="drink-expiry-banner" className="mt-5 rounded-2xl border border-[#D4AF37]/40 bg-[#D4AF37]/10 p-4 flex items-start gap-3">
          <AlertTriangle size={18} className="text-[#D4AF37] mt-0.5 shrink-0" />
          <div className="min-w-0">
            <p className="font-medium text-sm">Drink coupons expiring soon</p>
            <p className="text-[#A19DB0] text-xs mt-0.5">
              You still have {expiringDrinks.reduce((s, p) => s + (p.drink_left || 0), 0)} drink coupon{expiringDrinks.reduce((s, p) => s + (p.drink_left || 0), 0) !== 1 ? "s" : ""} to use — {expiringDrinks.map((p) => `${p.package_name} (${daysLeft(p.expires_at)}d)`).join(", ")}. Redeem them at the bar before they're gone!
            </p>
          </div>
        </div>
      )}
      <h2 className="font-display text-xl font-semibold mt-8 mb-3">Upcoming</h2>
      <div className="space-y-3" data-testid="dashboard-upcoming">
        <UpcomingRow testid="upcoming-social" label="Social" Icon={Wine} color="#D4AF37" title={oneSocial?.title} date={oneSocial ? fmtDateTime(oneSocial.starts_at) : ""} />
        <UpcomingRow testid="upcoming-class" label="Class" Icon={CalendarClock} color="#9D4EDD" title={oneClass?.class_title} date={oneClass ? fmtDateTime(oneClass.starts_at) : ""} />
        <UpcomingRow testid="upcoming-event" label="Event" Icon={Music} color="#EC4899" title={oneEvent?.title} date={oneEvent ? fmtDateTime(oneEvent.starts_at) : ""} />
      </div>
    </Screen>
  );
}
